import {
  airtelLogo,
  darkIkjNepaLogo,
  dstvLogo,
  egNepaLogo,
  etisalatLogo,
  gloLogo,
  gotvLogo,
  ibNepaLogo,
  ikjNepaLogo,
  mtnLogo,
  nepaLogo,
  showmaxLogo,
  startimesLogo,
} from "@/assets";
import AnimatedPage from "@/components/AnimatedPage";
import CategoryDrawer from "@/components/drawers/category-drawer";
import HeaderBox from "@/components/HeaderBox";
import HelmetMetaData from "@/components/HelmetMetaData";
import AddBeneficiaryModal from "@/components/modals/add-beneficiary-modal";
import { Skeleton } from "@/components/ui/skeleton";
import { useTheme } from "@/hooks/useTheme";
import { DeleteRequest, GetRequest } from "@/lib/http";
import { Loader2 } from "lucide-react";
import { Confirm } from "notiflix";
import React, { useEffect, useState } from "react";
import { BsPlusCircleFill } from "react-icons/bs";
import { FiEdit, FiTrash2 } from "react-icons/fi";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";

const Beneficiary = () => {
  const [category, setCategory] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editedItem, setEditedItem] = useState(null);

  const [beneficiariesData, setBeneficiariesData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");

  const fetchBeneficiary = async () => {
    const { data, err } = await GetRequest({
      url: "/api/beneficiary",
      setState: setLoading,
      errorMessage: "Error fetching beneficiaries",
    });

    if (!err) {
      setBeneficiariesData(data);
    } else {
      console.log(err);
    }
  };

  const deleteBeneficiary = async (id) => {
    Confirm.show(
      "Confirm Action",
      `Are you sure you want to delete this beneficiary?`,
      "Yes, Proceed",
      "Cancel",
      async () => {
        await DeleteRequest({
          url: `/api/beneficiary/${id}`,
        });
        fetchBeneficiary();
        toast.success("Beneficiary deleted successfully");
      },
    );
  };

  useEffect(() => {
    fetchBeneficiary();
  }, []);

  const networkImages = {
    mtn: mtnLogo,
    airtel: airtelLogo,
    glo: gloLogo,
    "9mobile": etisalatLogo,
  };

  const cableImages = {
    dstv: dstvLogo,
    gotv: gotvLogo,
    startimes: startimesLogo,
    showmax: showmaxLogo,
  };

  const discoImages = {
    ikeja: useTheme().theme === "dark" ? darkIkjNepaLogo : ikjNepaLogo,
    enugu: egNepaLogo,
    ibadan: ibNepaLogo,
    eko: nepaLogo,
  };

  const filteredBeneficiaries = beneficiariesData.filter(
    (beneficiary) =>
      (beneficiary.name?.toLowerCase() || "").includes(search.toLowerCase()) ||
      (beneficiary.value?.toLowerCase() || "").includes(search.toLowerCase()),
  );

  const handleEdit = (id) => {
    const filter = beneficiariesData.find((item) => item.id === id);
    setEditedItem(filter);
    setShowModal(true);
  };

  return (
    <AnimatedPage>
      <HelmetMetaData
        title="Purchase Data Plans"
        description="Access affordable mobile data plans effortlessly. Buy data packages online to keep your devices connected and enjoy seamless browsing!"
      />

      <section className="p-3 sm:p-5">
        <div className="flex flex-col-reverse items-start justify-between md:flex-row">
          <HeaderBox
            title="Beneficiary"
            subtext="Manage your beneficiaries with ease - add, edit, and track your data purchases!"
          />

          <div>
            <h2 className="text-sm font-medium">
              <Link to="/dashboard" className="hover:underline">
                Dashboard
              </Link>

              <span className="px-2 text-xl font-thin">/</span>

              <span className="text-gray-400">Beneficiary</span>
            </h2>
          </div>
        </div>

        <div className="mt-3">
          <input
            type="text"
            className="inputClass"
            placeholder="Search "
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <div
          onClick={() => setShowModal(true)}
          className="my-4 flex w-full cursor-pointer items-center justify-between rounded-2xl bg-gray-100 p-3.5 py-3 transition-all duration-300 ease-in-out hover:bg-gray-200/90 lg:w-[350px] dark:bg-[#252525]"
        >
          <div className="flex items-center gap-3">
            <BsPlusCircleFill size={25} className="text-primary" />

            <div>
              <h3 className="font-semibold">Add Beneficiary</h3>
              <p className="text-sm text-gray-600 dark:text-gray-200">
                Pay bills easier
              </p>
            </div>
          </div>

          <button
            onClick={() => setShowModal(true)}
            className="bg-primary cursor-pointer rounded-lg px-7 py-2 text-white"
          >
            Add
          </button>
        </div>

        {loading ? (
          <div className="flex flex-col space-y-3">
            {Array(3)
              .fill()
              .map((_, index) => (
                <Skeleton
                  key={index}
                  className="h-12 w-full rounded-md lg:w-[80%]"
                />
              ))}
          </div>
        ) : filteredBeneficiaries.length > 0 ? (
          <div className="flex flex-col gap-6">
            {filteredBeneficiaries
              ?.sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at))
              .map((item, index) => (
                <div key={index} className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <img
                      src={
                        item.type === "phone"
                          ? networkImages[item.network.toLowerCase()]
                          : item.type === "meter"
                            ? discoImages[item.disco.toLowerCase()]
                            : cableImages[item?.cable?.toLowerCase()]
                      }
                      alt="provider-logo"
                      className="size-14 rounded-full"
                    />

                    <div>
                      <h3 className="font-medium capitalize">{item.name}</h3>
                      <p className="text-sm text-gray-500">{item.value}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => handleEdit(item?.id)}
                      className="hover:text-primary cursor-pointer rounded-lg bg-gray-100 p-2 text-gray-600"
                    >
                      <FiEdit size={20} />
                    </button>

                    <button
                      onClick={() => deleteBeneficiary(item?.id)}
                      className="cursor-pointer rounded-lg bg-red-200 p-2 text-gray-600 hover:text-red-500"
                    >
                      <FiTrash2 size={20} className="text-red-500" />
                    </button>
                  </div>
                </div>
              ))}
          </div>
        ) : (
          <p className="mt-10 text-center text-gray-500 dark:text-gray-300">
            You currently have no beneficiaries
          </p>
        )}

        <CategoryDrawer
          setCategory={setCategory}
          showModal={showModal}
          setShowModal={setShowModal}
          setRefetch={fetchBeneficiary}
          editedItem={editedItem}
        />

        {/* <AddBeneficiaryModal
          open={showModal}
          onClose={() => {
            setShowModal(false);
            setEditedItem(null);
          }}
          editedItem={editedItem}
        /> */}
      </section>
    </AnimatedPage>
  );
};

export default Beneficiary;
